// Enhanced Position Management System
// File: src/employer/pages/Positions/PositionManager.js

import React, { useState, useEffect } from 'react';
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  getDoc,
  onSnapshot,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../../../configs/firebase';
import { EAuthData } from '../../Provider/EAuthProvider';
import { Formik, Form, Field, ErrorMessage, FieldArray } from 'formik';
import * as Yup from 'yup';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  FaPlus, 
  FaEdit, 
  FaTrash, 
  FaEye, 
  FaSave, 
  FaTimes, 
  FaCheck, 
  FaClock, 
  FaMapMarkerAlt,
  FaBriefcase,
  FaDollarSign,
  FaCalendar,
  FaUsers,
  FaTags,
  FaSearch,
  FaFilter,
  FaSort,
  FaExclamationTriangle
} from 'react-icons/fa';

// Position Form Component
const PositionForm = ({ mode = 'create', positionId = null, onSuccess, onCancel }) => {
  const { user } = EAuthData();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [areaOptions, setAreaOptions] = useState([]);
  const [position, setPosition] = useState(null);

  // Validation Schema
  const validationSchema = Yup.object({
    positionName: Yup.string()
      .required('Position name is required')
      .min(3, 'Position name must be at least 3 characters')
      .max(100, 'Position name must be less than 100 characters'),
    location: Yup.string()
      .required('Location is required')
      .min(2, 'Location must be at least 2 characters'),
    description: Yup.string()
      .required('Description is required')
      .min(50, 'Description must be at least 50 characters')
      .max(2000, 'Description must be less than 2000 characters'),
    type: Yup.string()
      .required('Position type is required')
      .oneOf(['Full-time', 'Part-time', 'Remote', 'Hybrid'], 'Invalid position type'),
    duration: Yup.string()
      .required('Duration is required')
      .min(3, 'Duration must be at least 3 characters'),
    requiredSkills: Yup.array()
      .min(1, 'At least one skill is required')
      .max(20, 'Maximum 20 skills allowed'),
    trainingToBeProvided: Yup.string()
      .required('Training information is required')
      .min(20, 'Training description must be at least 20 characters'),
    opensWhen: Yup.date()
      .required('Start date is required')
      .min(new Date(), 'Start date cannot be in the past'),
    areas: Yup.array()
      .min(1, 'At least one area/field is required')
      .max(10, 'Maximum 10 areas allowed'),
    salaryMin: Yup.number()
      .when('isPaid', {
        is: true,
        then: (schema) => schema.min(0, 'Minimum salary must be positive').required('Minimum salary is required'),
        otherwise: (schema) => schema.notRequired()
      }),
    salaryMax: Yup.number()
      .when('isPaid', {
        is: true,
        then: (schema) => schema
          .min(Yup.ref('salaryMin'), 'Maximum salary must be greater than minimum salary')
          .required('Maximum salary is required'),
        otherwise: (schema) => schema.notRequired()
      })
  });

  // Initial Values
  const getInitialValues = () => ({
    positionName: position?.name || '',
    location: position?.location || '',
    description: position?.description || '',
    type: position?.type || '',
    duration: position?.duration || '',
    areas: position?.fields || [],
    requiredSkills: position?.requiredSkills || [],
    trainingToBeProvided: position?.trainingToBeProvided || '',
    opensWhen: position?.opensWhen ? new Date(position.opensWhen).toISOString().split('T')[0] : '',
    isPaid: position?.isPaid || false,
    isAdvertised: position?.isAdvertised || false,
    salaryMin: position?.salaryMin || '',
    salaryMax: position?.salaryMax || '',
    applicationDeadline: position?.applicationDeadline ? new Date(position.applicationDeadline).toISOString().split('T')[0] : '',
    contactEmail: position?.contactEmail || user?.userd?.email || '',
    requirements: position?.requirements || '',
    benefits: position?.benefits || '',
    workSchedule: position?.workSchedule || '',
    remoteFriendly: position?.remoteFriendly || false
  });

  // Load position data for edit mode
  useEffect(() => {
    if (mode === 'edit' && positionId) {
      fetchPosition();
    }
    fetchAreaOptions();
  }, [mode, positionId]);

  const fetchPosition = async () => {
    try {
      setLoading(true);
      const positionDoc = await getDoc(doc(db, 'positions', positionId));
      if (positionDoc.exists()) {
        const positionData = { id: positionDoc.id, ...positionDoc.data() };
        setPosition(positionData);
      } else {
        alert('Position not found');
        onCancel && onCancel();
      }
    } catch (error) {
      console.error('Error fetching position:', error);
      alert('Error loading position data');
    } finally {
      setLoading(false);
    }
  };

  const fetchAreaOptions = () => {
    const docRef = doc(db, 'settings', 'areas');
    const unsubscribe = onSnapshot(
      docRef,
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          setAreaOptions(docSnapshot.data().areas || []);
        } else {
          // Default areas if settings don't exist
          setAreaOptions([
            { id: 'technology', label: 'Technology' },
            { id: 'marketing', label: 'Marketing' },
            { id: 'finance', label: 'Finance' },
            { id: 'design', label: 'Design' },
            { id: 'sales', label: 'Sales' },
            { id: 'hr', label: 'Human Resources' },
            { id: 'operations', label: 'Operations' },
            { id: 'research', label: 'Research' }
          ]);
        }
      },
      (error) => {
        console.error('Error fetching areas:', error);
        setAreaOptions([]);
      }
    );

    return () => unsubscribe();
  };

  // Form submission handler
  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setSubmitting(true);

      const positionData = {
        employer_id: user.userd.uid,
        name: values.positionName.trim(),
        location: values.location.trim(),
        description: values.description.trim(),
        type: values.type,
        duration: values.duration.trim(),
        fields: values.areas,
        requiredSkills: values.requiredSkills.filter(skill => skill.trim() !== ''),
        trainingToBeProvided: values.trainingToBeProvided.trim(),
        opensWhen: values.opensWhen,
        isPaid: values.isPaid,
        isAdvertised: values.isAdvertised,
        status: values.isAdvertised ? 'active' : 'inactive',
        salaryMin: values.isPaid ? Number(values.salaryMin) : null,
        salaryMax: values.isPaid ? Number(values.salaryMax) : null,
        applicationDeadline: values.applicationDeadline || null,
        contactEmail: values.contactEmail.trim(),
        requirements: values.requirements?.trim() || '',
        benefits: values.benefits?.trim() || '',
        workSchedule: values.workSchedule?.trim() || '',
        remoteFriendly: values.remoteFriendly,
        updatedAt: serverTimestamp()
      };

      if (mode === 'create') {
        positionData.createdAt = serverTimestamp();
        await addDoc(collection(db, 'positions'), positionData);
        alert('Position created successfully!');
      } else {
        await updateDoc(doc(db, 'positions', positionId), positionData);
        alert('Position updated successfully!');
      }

      onSuccess && onSuccess();
    } catch (error) {
      console.error('Error saving position:', error);
      alert('Error saving position. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white rounded-lg shadow-lg p-6 md:p-8">
        <div className="flex justify-between items-center mb-8">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
            {mode === 'create' ? 'Create New Position' : 'Edit Position'}
          </h2>
          {onCancel && (
            <button
              onClick={onCancel}
              className="text-gray-500 hover:text-gray-700"
            >
              <FaTimes className="text-xl" />
            </button>
          )}
        </div>

        <Formik
          initialValues={getInitialValues()}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize={true}
        >
          {({ values, setFieldValue, isSubmitting, errors, touched }) => (
            <Form className="space-y-8">
              {/* Basic Information */}
              <div className="border-b border-gray-200 pb-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">Basic Information</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Position Name *
                    </label>
                    <Field
                      name="positionName"
                      type="text"
                      placeholder="e.g., Software Engineering Intern"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                    <ErrorMessage name="positionName" component="div" className="text-red-500 text-sm mt-1" />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Location *
                    </label>
                    <Field
                      name="location"
                      type="text"
                      placeholder="e.g., New York, NY or Remote"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                    <ErrorMessage name="location" component="div" className="text-red-500 text-sm mt-1" />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Position Type *
                    </label>
                    <Field
                      as="select"
                      name="type"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    >
                      <option value="">Select Type</option>
                      <option value="Full-time">Full-time</option>
                      <option value="Part-time">Part-time</option>
                      <option value="Remote">Remote</option>
                      <option value="Hybrid">Hybrid</option>
                    </Field>
                    <ErrorMessage name="type" component="div" className="text-red-500 text-sm mt-1" />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Duration *
                    </label>
                    <Field
                      name="duration"
                      type="text"
                      placeholder="e.g., 3 months, Summer 2025"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                    <ErrorMessage name="duration" component="div" className="text-red-500 text-sm mt-1" />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Start Date *
                    </label>
                    <Field
                      name="opensWhen"
                      type="date"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                    <ErrorMessage name="opensWhen" component="div" className="text-red-500 text-sm mt-1" />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Application Deadline
                    </label>
                    <Field
                      name="applicationDeadline"
                      type="date"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>
                </div>
              </div>

              {/* Description and Details */}
              <div className="border-b border-gray-200 pb-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">Position Details</h3>
                
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Position Description *
                    </label>
                    <Field
                      as="textarea"
                      name="description"
                      rows="5"
                      placeholder="Describe the role, responsibilities, and what the intern will learn..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                    <ErrorMessage name="description" component="div" className="text-red-500 text-sm mt-1" />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Training to be Provided *
                    </label>
                    <Field
                      as="textarea"
                      name="trainingToBeProvided"
                      rows="3"
                      placeholder="Describe the training, mentorship, and learning opportunities..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                    <ErrorMessage name="trainingToBeProvided" component="div" className="text-red-500 text-sm mt-1" />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Requirements
                    </label>
                    <Field
                      as="textarea"
                      name="requirements"
                      rows="3"
                      placeholder="Additional requirements, qualifications, or prerequisites..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Benefits & Perks
                    </label>
                    <Field
                      as="textarea"
                      name="benefits"
                      rows="3"
                      placeholder="Benefits, perks, networking opportunities, etc..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Work Schedule
                    </label>
                    <Field
                      name="workSchedule"
                      type="text"
                      placeholder="e.g., Monday-Friday 9am-5pm, Flexible hours"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>
                </div>
              </div>

              {/* Skills and Fields */}
              <div className="border-b border-gray-200 pb-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">Skills & Areas</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Required Skills */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Required Skills *
                    </label>
                    <FieldArray name="requiredSkills">
                      {({ push, remove }) => (
                        <div className="space-y-2">
                          {values.requiredSkills.map((skill, index) => (
                            <div key={index} className="flex items-center space-x-2">
                              <Field
                                name={`requiredSkills.${index}`}
                                type="text"
                                placeholder="Enter a skill"
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                              />
                              <button
                                type="button"
                                onClick={() => remove(index)}
                                className="text-red-500 hover:text-red-700"
                              >
                                <FaTimes />
                              </button>
                            </div>
                          ))}
                          <button
                            type="button"
                            onClick={() => push('')}
                            className="flex items-center text-orange-600 hover:text-orange-800 text-sm font-medium"
                          >
                            <FaPlus className="mr-1" />
                            Add Skill
                          </button>
                        </div>
                      )}
                    </FieldArray>
                    <ErrorMessage name="requiredSkills" component="div" className="text-red-500 text-sm mt-1" />
                  </div>

                  {/* Areas/Fields */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Areas/Fields *
                    </label>
                    <div className="space-y-2 max-h-48 overflow-y-auto border border-gray-300 rounded-md p-3">
                      {areaOptions.map((area) => (
                        <label key={area.id} className="flex items-center">
                          <Field
                            type="checkbox"
                            name="areas"
                            value={area.id}
                            className="h-4 w-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                          />
                          <span className="ml-2 text-sm text-gray-700">{area.label}</span>
                        </label>
                      ))}
                    </div>
                    <ErrorMessage name="areas" component="div" className="text-red-500 text-sm mt-1" />
                  </div>
                </div>
              </div>

              {/* Compensation and Settings */}
              <div className="border-b border-gray-200 pb-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">Compensation & Settings</h3>
                
                <div className="space-y-6">
                  {/* Paid Position Toggle */}
                  <div className="flex items-center space-x-3">
                    <Field
                      type="checkbox"
                      name="isPaid"
                      className="h-4 w-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                    />
                    <label className="text-sm font-medium text-gray-700">
                      This is a paid position
                    </label>
                  </div>

                  {/* Salary Range (conditional) */}
                  {values.isPaid && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Minimum Salary (per month) *
                        </label>
                        <Field
                          name="salaryMin"
                          type="number"
                          placeholder="e.g., 1000"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        />
                        <ErrorMessage name="salaryMin" component="div" className="text-red-500 text-sm mt-1" />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Maximum Salary (per month) *
                        </label>
                        <Field
                          name="salaryMax"
                          type="number"
                          placeholder="e.g., 2000"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        />
                        <ErrorMessage name="salaryMax" component="div" className="text-red-500 text-sm mt-1" />
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Contact Email
                    </label>
                    <Field
                      name="contactEmail"
                      type="email"
                      placeholder="Contact email for applications"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>

                  {/* Additional Settings */}
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3">
                      <Field
                        type="checkbox"
                        name="isAdvertised"
                        className="h-4 w-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                      />
                      <label className="text-sm font-medium text-gray-700">
                        Publish this position immediately
                      </label>
                    </div>

                    <div className="flex items-center space-x-3">
                      <Field
                        type="checkbox"
                        name="remoteFriendly"
                        className="h-4 w-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                      />
                      <label className="text-sm font-medium text-gray-700">
                        Remote-friendly position
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              {/* Form Actions */}
              <div className="flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-4">
                {onCancel && (
                  <button
                    type="button"
                    onClick={onCancel}
                    className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 font-medium"
                  >
                    Cancel
                  </button>
                )}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-6 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center justify-center"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      {mode === 'create' ? 'Creating...' : 'Updating...'}
                    </>
                  ) : (
                    <>
                      <FaSave className="mr-2" />
                      {mode === 'create' ? 'Create Position' : 'Update Position'}
                    </>
                  )}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

// Positions List Component
const PositionsList = () => {
  const { user } = EAuthData();
  const navigate = useNavigate();
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');

  // Fetch positions
  useEffect(() => {
    if (user?.userd?.uid) {
      fetchPositions();
    }
  }, [user]);

  const fetchPositions = async () => {
    try {
      setLoading(true);
      const positionsRef = collection(db, 'positions');
      const q = query(
        positionsRef,
        where('employer_id', '==', user.userd.uid),
        orderBy(sortBy, sortOrder)
      );
      
      const querySnapshot = await getDocs(q);
      const positionsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setPositions(positionsData);
    } catch (error) {
      console.error('Error fetching positions:', error);
      alert('Error loading positions');
    } finally {
      setLoading(false);
    }
  };

  // Filter and sort positions
  const filteredPositions = positions.filter(position => {
    const matchesSearch = position.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         position.location?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         position.description?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = filterStatus === 'all' || 
                         (filterStatus === 'active' && position.isAdvertised) ||
                         (filterStatus === 'inactive' && !position.isAdvertised) ||
                         (filterStatus === 'paid' && position.isPaid) ||
                         (filterStatus === 'unpaid' && !position.isPaid);
    
    return matchesSearch && matchesFilter;
  });

  // Delete position
  const handleDelete = async (positionId) => {
    if (window.confirm('Are you sure you want to delete this position? This action cannot be undone.')) {
      try {
        await deleteDoc(doc(db, 'positions', positionId));
        setPositions(prev => prev.filter(p => p.id !== positionId));
        alert('Position deleted successfully');
      } catch (error) {
        console.error('Error deleting position:', error);
        alert('Error deleting position');
      }
    }
  };

  // Toggle position status
  const toggleStatus = async (positionId, currentStatus) => {
    try {
      const newStatus = !currentStatus;
      await updateDoc(doc(db, 'positions', positionId), {
        isAdvertised: newStatus,
        status: newStatus ? 'active' : 'inactive',
        updatedAt: serverTimestamp()
      });
      
      setPositions(prev =>
        prev.map(p =>
          p.id === positionId
            ? { ...p, isAdvertised: newStatus, status: newStatus ? 'active' : 'inactive' }
            : p
        )
      );
    } catch (error) {
      console.error('Error updating position status:', error);
      alert('Error updating position status');
    }
  };

  const getStatusColor = (isAdvertised) => {
    return isAdvertised 
      ? 'bg-green-100 text-green-800 border-green-200'
      : 'bg-yellow-100 text-yellow-800 border-yellow-200';
  };

  const getTypeColor = (type) => {
    const colors = {
      'Full-time': 'bg-blue-100 text-blue-800',
      'Part-time': 'bg-purple-100 text-purple-800',
      'Remote': 'bg-green-100 text-green-800',
      'Hybrid': 'bg-orange-100 text-orange-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading positions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">Manage Positions</h1>
            <p className="text-gray-600">Create, edit, and manage your internship positions</p>
          </div>
          <button
            onClick={() => navigate('/employer/positions/add')}
            className="mt-4 md:mt-0 bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center"
          >
            <FaPlus className="mr-2" />
            Add New Position
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-8">
          <div className="bg-white p-4 md:p-6 rounded-lg shadow-md border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xs md:text-sm font-medium text-gray-600">Total Positions</h3>
                <p className="text-xl md:text-2xl font-bold text-indigo-600">{positions.length}</p>
              </div>
              <div className="bg-indigo-100 p-2 md:p-3 rounded-full">
                <FaBriefcase className="h-4 md:h-6 w-4 md:w-6 text-indigo-600" />
              </div>
            </div>
          </div>

          <div className="bg-white p-4 md:p-6 rounded-lg shadow-md border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xs md:text-sm font-medium text-gray-600">Active</h3>
                <p className="text-xl md:text-2xl font-bold text-green-600">
                  {positions.filter(p => p.isAdvertised).length}
                </p>
              </div>
              <div className="bg-green-100 p-2 md:p-3 rounded-full">
                <FaCheck className="h-4 md:h-6 w-4 md:w-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white p-4 md:p-6 rounded-lg shadow-md border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xs md:text-sm font-medium text-gray-600">Inactive</h3>
                <p className="text-xl md:text-2xl font-bold text-yellow-600">
                  {positions.filter(p => !p.isAdvertised).length}
                </p>
              </div>
              <div className="bg-yellow-100 p-2 md:p-3 rounded-full">
                <FaClock className="h-4 md:h-6 w-4 md:w-6 text-yellow-600" />
              </div>
            </div>
          </div>

          <div className="bg-white p-4 md:p-6 rounded-lg shadow-md border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xs md:text-sm font-medium text-gray-600">Paid</h3>
                <p className="text-xl md:text-2xl font-bold text-purple-600">
                  {positions.filter(p => p.isPaid).length}
                </p>
              </div>
              <div className="bg-purple-100 p-2 md:p-3 rounded-full">
                <FaDollarSign className="h-4 md:h-6 w-4 md:w-6 text-purple-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="bg-white p-4 md:p-6 rounded-lg shadow-md border border-gray-200 mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search positions..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 w-full sm:w-auto"
                />
              </div>
              
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                <option value="all">All Positions</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="paid">Paid</option>
                <option value="unpaid">Unpaid</option>
              </select>
            </div>

            <div className="flex items-center space-x-2">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                <option value="createdAt">Created Date</option>
                <option value="name">Position Name</option>
                <option value="opensWhen">Start Date</option>
                <option value="location">Location</option>
              </select>
              
              <button
                onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                className="p-2 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                <FaSort className={`text-gray-600 ${sortOrder === 'desc' ? 'transform rotate-180' : ''}`} />
              </button>
            </div>
          </div>
        </div>

        {/* Positions List */}
        <div className="space-y-6">
          {filteredPositions.length === 0 ? (
            <div className="text-center py-12">
              <FaBriefcase className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {positions.length === 0 ? 'No positions created yet' : 'No positions match your filters'}
              </h3>
              <p className="text-gray-600 mb-4">
                {positions.length === 0 
                  ? 'Create your first internship position to get started'
                  : 'Try adjusting your search or filter criteria'
                }
              </p>
              {positions.length === 0 && (
                <button
                  onClick={() => navigate('/employer/positions/add')}
                  className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-2 rounded-md font-medium"
                >
                  Create First Position
                </button>
              )}
            </div>
          ) : (
            filteredPositions.map(position => (
              <div key={position.id} className="bg-white rounded-lg shadow-md border border-gray-200 p-4 md:p-6">
                <div className="flex flex-col lg:flex-row justify-between items-start mb-4">
                  <div className="flex-1 mb-4 lg:mb-0">
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                      <h3 className="text-lg md:text-xl font-semibold text-gray-800">{position.name}</h3>
                      
                      <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(position.isAdvertised)}`}>
                        {position.isAdvertised ? 'Active' : 'Inactive'}
                      </span>
                      
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(position.type)}`}>
                        {position.type}
                      </span>
                      
                      {position.isPaid && (
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Paid
                        </span>
                      )}
                      
                      {position.remoteFriendly && (
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          Remote Friendly
                        </span>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm text-gray-600 mb-3">
                      <div className="flex items-center">
                        <FaMapMarkerAlt className="mr-2 text-gray-400" />
                        {position.location}
                      </div>
                      <div className="flex items-center">
                        <FaCalendar className="mr-2 text-gray-400" />
                        Starts: {new Date(position.opensWhen).toLocaleDateString()}
                      </div>
                      <div className="flex items-center">
                        <FaClock className="mr-2 text-gray-400" />
                        {position.duration}
                      </div>
                      {position.isPaid && position.salaryMin && (
                        <div className="flex items-center">
                          <FaDollarSign className="mr-2 text-gray-400" />
                          ${position.salaryMin} - ${position.salaryMax}/mo
                        </div>
                      )}
                    </div>
                    
                    <p className="text-gray-700 line-clamp-2 mb-3">{position.description}</p>
                    
                    {position.requiredSkills && position.requiredSkills.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-3">
                        {position.requiredSkills.slice(0, 5).map((skill, index) => (
                          <span key={index} className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">
                            {skill}
                          </span>
                        ))}
                        {position.requiredSkills.length > 5 && (
                          <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">
                            +{position.requiredSkills.length - 5} more
                          </span>
                        )}
                      </div>
                    )}
                    
                    <div className="text-xs text-gray-500">
                      Created: {position.createdAt ? new Date(position.createdAt.toDate()).toLocaleDateString() : 'N/A'}
                      {position.updatedAt && (
                        <span className="ml-4">
                          Updated: {new Date(position.updatedAt.toDate()).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      onClick={() => navigate(`/employer/positions/view/${position.id}`)}
                      className="px-3 py-1 bg-blue-100 text-blue-700 rounded text-sm hover:bg-blue-200 flex items-center"
                    >
                      <FaEye className="mr-1" />
                      View
                    </button>
                    
                    <button
                      onClick={() => navigate(`/employer/positions/edit/${position.id}`)}
                      className="px-3 py-1 bg-gray-100 text-gray-700 rounded text-sm hover:bg-gray-200 flex items-center"
                    >
                      <FaEdit className="mr-1" />
                      Edit
                    </button>
                    
                    <button
                      onClick={() => toggleStatus(position.id, position.isAdvertised)}
                      className={`px-3 py-1 rounded text-sm flex items-center ${
                        position.isAdvertised
                          ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                          : 'bg-green-100 text-green-700 hover:bg-green-200'
                      }`}
                    >
                      {position.isAdvertised ? (
                        <>
                          <FaClock className="mr-1" />
                          Deactivate
                        </>
                      ) : (
                        <>
                          <FaCheck className="mr-1" />
                          Activate
                        </>
                      )}
                    </button>
                    
                    <button
                      onClick={() => handleDelete(position.id)}
                      className="px-3 py-1 bg-red-100 text-red-700 rounded text-sm hover:bg-red-200 flex items-center"
                    >
                      <FaTrash className="mr-1" />
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

// Main Position Manager Component
const PositionManager = ({ mode = 'list', positionId = null }) => {
  const navigate = useNavigate();

  const handleSuccess = () => {
    navigate('/employer/positions');
  };

  const handleCancel = () => {
    navigate('/employer/positions');
  };

  if (mode === 'list') {
    return <PositionsList />;
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <PositionForm 
        mode={mode} 
        positionId={positionId}
        onSuccess={handleSuccess}
        onCancel={handleCancel}
      />
    </div>
  );
};

// Individual Page Components for Routing
export const AddPosition = () => <PositionManager mode="create" />;
export const EditPosition = () => {
  const { id } = useParams();
  return <PositionManager mode="edit" positionId={id} />;
};
export const ViewPositions = () => <PositionManager mode="list" />;

export default PositionManager;